import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

// Reusable, presentation-only primitives shared by the new report pages
// (Daily Mud Report, Trip Sheet, Bit Run Record). DDR keeps its own inline
// copies untouched — these primitives are additive only.

export function Field({ label, helper, children, testId }) {
  return (
    <label className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-[11px] font-semibold text-[var(--rs-text-2)] tracking-wide">{label}</span>
      {children}
      {helper && <span className="text-[10px] text-[var(--rs-text-3)] leading-snug">{helper}</span>}
    </label>
  );
}

// Opt-in error state, added for the Rig Data Sheet editor's failed-save
// marking. Purely additive: `invalid` defaults to undefined, and when it is
// absent both primitives below render exactly as they did before — no style
// attribute added to TextInput, TextArea's existing inline style untouched,
// and no aria-invalid attribute emitted. No existing caller passes it.
const INVALID_STYLE = {
  borderColor: "#FCA5A5",
  background: "rgba(252,165,165,0.06)",
};

// Split date+time control (follow-up, ticket 237121) — extracted from the
// BHA/Drill String editors' identical inline copy (Five-Defect Correction
// Pass, Defect 4). A single `type="datetime-local"` input has inconsistent
// native-picker support on mobile (older Android WebViews / Firefox Mobile
// fall back to raw text entry). Splitting into separate `type="date"` +
// `type="time"` native inputs has near-universal mobile picker support and
// still combines into the SAME stored "YYYY-MM-DDTHH:mm" string this field
// always used — no schema/format/validation change.
export function DateTimeSplitInput({ value, onChange, testId }) {
  const [datePart, timePart] = String(value || "").split("T");
  return (
    <div className="flex gap-2">
      <input type="date" value={datePart || ""}
             onChange={(e) => onChange(e.target.value ? `${e.target.value}T${timePart || "00:00"}` : "")}
             data-testid={`${testId}-date`} className="input-rs flex-1" />
      <input type="time" value={timePart || ""} disabled={!datePart}
             onChange={(e) => onChange(`${datePart}T${e.target.value || "00:00"}`)}
             data-testid={`${testId}-time`} className="input-rs flex-1" />
    </div>
  );
}

export function TextInput({ value, onChange, type = "text", placeholder, testId, disabled, invalid }) {
  // RC-1 Data Integrity Fix — for numeric inputs, blur on wheel so the
  // browser cannot silently increment/decrement the value while the operator
  // is scrolling the page.  Non-numeric inputs are unaffected (no-op).
  const guardedOnWheel = type === "number"
    ? (e) => { if (e.currentTarget === document.activeElement) e.currentTarget.blur(); }
    : undefined;
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onWheel={guardedOnWheel}
      placeholder={placeholder}
      data-testid={testId}
      disabled={disabled}
      aria-invalid={invalid ? true : undefined}
      className="input-rs"
      style={invalid ? INVALID_STYLE : undefined}
    />
  );
}

export function TextArea({ value, onChange, rows = 3, placeholder, testId, disabled, invalid }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      data-testid={testId}
      disabled={disabled}
      aria-invalid={invalid ? true : undefined}
      className="input-rs"
      style={invalid
        ? { minHeight: 64, resize: "vertical", ...INVALID_STYLE }
        : { minHeight: 64, resize: "vertical" }}
    />
  );
}

export function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="input-rs"
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export function Checkbox({ checked, onChange, label, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-[12px] text-[var(--rs-text-2)]" data-testid={testId}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function Section({ id, title, badge, open, onToggle, children }) {
  return (
    <section data-testid={`report-section-${id}`} className="card-rs" style={{ padding: 0, overflow: "hidden" }}>
      <button
        type="button"
        data-testid={`report-section-toggle-${id}`}
        onClick={onToggle}
        className="w-full px-3.5 py-3 flex items-center justify-between text-left hover:bg-[rgba(255,179,0,0.04)] transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="font-mono-rs text-[9px] tracking-[0.18em] uppercase px-1.5 py-[1px] rounded-[3px] font-bold"
            style={{
              background: "rgba(255,179,0,0.14)",
              color: "var(--rs-amber)",
              border: "1px solid rgba(255,179,0,0.4)",
            }}
          >
            {badge}
          </span>
          <span className="font-heading font-black text-sm tracking-tight truncate">{title}</span>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3.5 pb-4 pt-1 border-t border-[var(--rs-border)] flex flex-col gap-3">{children}</div>
      )}
    </section>
  );
}

export function DraftDisclaimer({ testId = "report-disclaimer" }) {
  return (
    <div
      data-testid={testId}
      className="card-rs p-3 flex items-start gap-2.5"
      style={{
        background: "rgba(22,24,29,0.65)",
        borderColor: "var(--rs-border)",
        borderLeft: "2px solid var(--rs-amber)",
      }}
    >
      <span
        className="font-mono-rs text-[9px] tracking-[0.18em] uppercase px-1.5 py-[1px] rounded-[3px] font-bold mt-[2px] shrink-0"
        style={{
          background: "rgba(255,179,0,0.14)",
          color: "var(--rs-amber)",
          border: "1px solid rgba(255,179,0,0.4)",
        }}
      >
        Draft
      </span>
      <p className="text-[11px] leading-snug text-[var(--rs-text-3)]">
        RigSentinel report tools provide draft documentation support only. All reports must be reviewed, corrected, and approved by competent rig/site personnel under company procedures, client requirements, and applicable regulations before submission.
      </p>
    </div>
  );
}
